import React from 'react'
import { Box, Grid, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { T } from './onboardingDesign'
import { FieldLabel } from './onboardingUi'

/**
 * One restaurant's Schedule A fees (backend utils/agreementFees.js), edited
 * as strings. A fee that differs from the standard is highlighted with the
 * standard under it. Used by Start an application, the application review
 * and the agent portal.
 *
 * The standard Order Percentages, Service Fee and Third-Party Orders % are
 * minimums (FLOOR_KEYS): an agent or ISO may go higher on their own; lower,
 * or any other fee changed, needs a super admin (needsApproval). With
 * minimums on, the fields say so.
 */

export const FEE_FIELDS = [
  ['deliveryCommissionRate', 'Delivery orders', 'percent'],
  ['selfDeliveryCommissionRate', 'Self-delivery orders', 'percent'],
  ['pickupCommissionRate', 'Pickup orders', 'percent'],
  ['thirdPartyPercent', 'Third-party orders we deliver', 'percent'],
  ['serviceFee', 'Service fee (customers pay, per order)', 'money'],
  ['deliveryFeeMin', 'Delivery fee (minimum)', 'money'],
  ['minimumOrder', 'Minimum order amount', 'money'],
  ['returnedPaymentFee', 'Returned payment fee', 'money'],
  ['statementFee', 'Monthly statement / account fee', 'money'],
  ['processingPercent', 'Payment processing', 'percent'],
  ['processingFixed', '+ per transaction', 'money'],
]
export const FEE_QUERY_FIELDS = FEE_FIELDS.map(([k]) => k).join(' ')
// Their standard is a minimum: higher needs nobody, lower needs a super admin.
export const FLOOR_KEYS = ['deliveryCommissionRate', 'selfDeliveryCommissionRate', 'pickupCommissionRate', 'thirdPartyPercent', 'serviceFee']
const ORDER_KEYS = FLOOR_KEYS.slice(0, 3)
const PLATFORM_KEYS = ['thirdPartyPercent', 'serviceFee']

const num = v => Number(String(v == null ? '' : v).replace(/[$,%\s]/g, ''))

export const showFee = (kind, v) => (v == null || v === '' ? '—' : kind === 'percent' ? `${Number(Number(v).toFixed(3))}%` : `$${Number(v).toFixed(2)}`)

export function feesToForm(fees) {
  return Object.fromEntries(FEE_FIELDS.map(([k]) => [k, fees && fees[k] != null ? String(fees[k]) : '']))
}

export function formToFees(form) {
  return Object.fromEntries(FEE_FIELDS.map(([k]) => [k, num(form[k])]))
}

export function feeErrors(form) {
  const errors = {}
  for (const [k, , kind] of FEE_FIELDS) {
    const n = num(form[k])
    // A restaurant's service fee is $0 to $20 (backend stripeOrders.validServiceFee).
    const max = kind === 'percent' ? 100 : k === 'serviceFee' ? 20 : 10000
    if (form[k] === '' || !Number.isFinite(n) || n < 0 || n > max) {
      errors[k] = kind === 'percent' ? '0 to 100' : k === 'serviceFee' ? '$0 to $20' : '$0 to $10,000'
    }
  }
  return errors
}

/** The minimum fees set below the standard. */
export function belowMinimum(fees, standard) {
  if (!fees || !standard) return []
  return FEE_FIELDS.filter(([k]) => FLOOR_KEYS.includes(k) && Number(fees[k]) < Number(standard[k]) - 1e-9)
}

/** An agent's fees need a super admin: one below a minimum, or any other fee changed. */
export function needsApproval(fees, standard) {
  if (!fees || !standard) return false
  return belowMinimum(fees, standard).length > 0
    || FEE_FIELDS.some(([k]) => !FLOOR_KEYS.includes(k) && Number(fees[k]) !== Number(standard[k]))
}

export function changedFees(fees, standard) {
  if (!fees || !standard) return []
  return FEE_FIELDS.filter(([k]) => fees[k] != null && Number(fees[k]) !== Number(standard[k]))
}

/** "Delivery orders 18% (standard 30%) · …" for the fees that differ. */
export function describeChanges(fees, standard) {
  const changed = changedFees(fees, standard)
  if (!changed.length) return 'Standard fees'
  return changed.map(([k, label, kind]) => `${label} ${showFee(kind, fees[k])} (standard ${showFee(kind, standard[k])})`).join(' · ')
}

/** This restaurant's fees next to the standard; the ones that differ in bold. */
export function FeeCompareTable({ fees, standard, label = 'This restaurant' }) {
  return (
    <Box sx={{ overflowX: 'auto', border: `1px solid ${T.cardBorder}`, borderRadius: '14px' }}>
      <Table size="small" sx={{ minWidth: 280, '& tr:last-child td': { borderBottom: 0 }, '& td, & th': { px: 1.5 } }}>
        <TableHead>
          <TableRow sx={{ background: T.footerBg }}>
            <TableCell sx={{ fontWeight: 600, color: T.muted }}>Fee</TableCell>
            <TableCell sx={{ fontWeight: 600, color: T.muted }}>{label}</TableCell>
            <TableCell sx={{ fontWeight: 600, color: T.muted }}>Standard</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {FEE_FIELDS.map(([k, name, kind]) => {
            const differs = fees && standard && Number(fees[k]) !== Number(standard[k])
            return (
              <TableRow key={k} sx={differs ? { bgcolor: T.amberTint } : undefined}>
                <TableCell>{k === 'processingFixed' ? 'Payment processing, per transaction' : k.endsWith('CommissionRate') ? `Order % — ${name.replace(' orders', '').toLowerCase()}` : name}{fees && standard && FLOOR_KEYS.includes(k) && Number(fees[k]) < Number(standard[k]) ? ' (below the minimum)' : ''}</TableCell>
                <TableCell sx={{ fontWeight: differs ? 700 : 400 }}>{showFee(kind, fees ? fees[k] : standard && standard[k])}</TableCell>
                <TableCell sx={{ color: T.hint }}>{showFee(kind, standard && standard[k])}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

/**
 * minimums: an agent's or ISO's editor, which says what each minimum is and
 * which fees need a super admin. readOnly: keys shown but not edited here.
 */
export default function FeeFields({ value, onChange, standard, disabled, minimums = false, readOnly = [] }) {
  const errors = feeErrors(value)
  const field = ([k, label, kind], sm) => {
    const differs = standard && value[k] !== '' && num(value[k]) !== Number(standard[k])
    const floor = minimums && standard && FLOOR_KEYS.includes(k)
    const below = floor && value[k] !== '' && num(value[k]) < Number(standard[k])
    const hint = !standard ? ' '
      : below ? `Below the ${showFee(kind, standard[k])} minimum: needs approval`
        : floor ? `Minimum ${showFee(kind, standard[k])}`
          : minimums && differs ? `Standard ${showFee(kind, standard[k])}: changing it needs approval`
            : differs ? `Standard: ${showFee(kind, standard[k])}` : ' '
    return (
      <Grid item xs={12} sm={sm} key={k}>
        <FieldLabel htmlFor={`fee-${k}`} label={label} />
        <TextField
          fullWidth size="small" id={`fee-${k}`} value={value[k]} disabled={disabled || readOnly.includes(k)}
          onChange={e => onChange({ ...value, [k]: e.target.value })}
          error={!!errors[k] || !!below}
          helperText={errors[k] || hint}
          inputProps={{ inputMode: 'decimal' }}
          InputProps={kind === 'percent'
            ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
            : { startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={differs ? { '& .MuiOutlinedInput-root': { bgcolor: T.amberTint }, '& .MuiFormHelperText-root': { color: T.amberText } } : undefined}
        />
      </Grid>
    )
  }
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.text, mb: 1.5 }}>Order percentage (% of subtotal)</Typography>
      <Grid container rowSpacing={0.5} columnSpacing={2} sx={{ mb: 1 }}>
        {FEE_FIELDS.filter(([k]) => ORDER_KEYS.includes(k)).map(f => field(f, 4))}
      </Grid>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.text, mb: 1.5, pt: 2, borderTop: `1px solid ${T.divider}` }}>Third-party orders and service fee</Typography>
      <Grid container rowSpacing={0.5} columnSpacing={2} sx={{ mb: 1 }}>
        {FEE_FIELDS.filter(([k]) => PLATFORM_KEYS.includes(k)).map(f => field(f, 6))}
      </Grid>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.text, mb: 1.5, pt: 2, borderTop: `1px solid ${T.divider}` }}>Other fees</Typography>
      <Grid container rowSpacing={0.5} columnSpacing={2}>
        {FEE_FIELDS.filter(([k]) => !FLOOR_KEYS.includes(k)).map(f => field(f, 6))}
      </Grid>
    </Box>
  )
}
